import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs/internal/Observable';
import {ApplyCouponResponse, CouponResponse, MembershipInfo, Plans, SetupIntentResponse, UpgradePayload} from '../models/SubscriptionsModels';
import {UserModel} from '../models/User';
import {MixpanelService} from './mixpanel.service';


const STRIPE_URL = '/api/stripe';
const HOTEL_CHECKOUT_PAYMENT_URL = '/api/hotels/checkout-payment';
const PLANS_URL = '/api/plans';
const PRODUCTS_URL = '/api/products';
const USER_MEMBERSHIP_INFO = `${STRIPE_URL}/user-membership`
const DOWNGRADE_TO_FREE_URL = `${STRIPE_URL}/downgrade`;
const APPLY_COUPON_URL = `${STRIPE_URL}/apply-coupon`
const GET_PRORATION = `${STRIPE_URL}/proration`

export enum Memberships {
    Free = 'free',
    Premium = 'premium',
    PremiumPlus = 'premiumPlus'
}
export enum TrialCodes {
    TwoWeekTrial = 'p2WkZisyJ',
    OneMonthTrial = 'p1AkZisyJ',
    ThreeMonthTrial = 'p3oUmcydb',
    SixMonthTrial = 'p6TbprZPt'
}

export enum ProductClass {
    DFC = 'dfc',
    PERKS = 'perks'
}
export enum ProductTier {
    free = 0,
    premium = 1,
    premiumPlus = 2,
    conciergeUser = 2
}



@Injectable({
    providedIn: 'root',
})
export class SubscriptionService {
    constructor(private http: HttpClient, private mixpanel: MixpanelService) {}

    setupIntent(user: UserModel): Observable<SetupIntentResponse> {
        return this.http.post<SetupIntentResponse>(`${STRIPE_URL}/setup-intent`, user, {withCredentials: true});
    }

    // dfcbookhotels only: creates the shadow account from `email` when there's
    // no session yet (ignored otherwise), then mints the SetupIntent — one
    // call instead of a separate "create account" step. A 409 means the
    // email already has an account; the caller surfaces it inline.
    setupIntentForHotelCheckout(email?: string): Observable<SetupIntentResponse> {
        return this.http.post<SetupIntentResponse>(`${HOTEL_CHECKOUT_PAYMENT_URL}/setup-intent`, {email}, {withCredentials: true});
    }

    // Account only — the checkout rail's Continue-with-Premium+ gate needs
    // the shadow session before it can run the member preview, but no
    // SetupIntent yet (the trial widget mints its own at submit). Same 409
    // contract as above.
    ensureHotelCheckoutAccount(email: string): Observable<{user: UserModel}> {
        return this.http.post<{user: UserModel}>(`${HOTEL_CHECKOUT_PAYMENT_URL}/account`, {email}, {withCredentials: true});
    }

    getPlan(body :{ partner: string, product: string, isTrial?: boolean}): Observable<Plans> {
        const {partner, product, isTrial} = body
        let url = `${PLANS_URL}/${encodeURIComponent(partner)}/${encodeURIComponent(product)}`;
        if (isTrial !== undefined) {
            url += `?isTrial=${isTrial}`;
        }
        return this.http.get<Plans>(url);
    }

    checkCoupon(coupon: string): Observable<CouponResponse> {
        return this.http.get<CouponResponse>(`${STRIPE_URL}/coupon`, {params: {coupon: coupon}});
    }

    // The response body is the updated UserModel (StripeController.upgrade
    // sends it directly via res.send(user)) — not an echo of the request.
    upgradePlan(body: UpgradePayload): Observable<UserModel> {
        return this.http.post<UserModel>(`${STRIPE_URL}/upgrade`, body, {withCredentials: true});
    }
    updateCardInfo(paymentMethod: string): Observable<void>{
        return this.http.post<void>(`${STRIPE_URL}/card/update`, {paymentMethod: paymentMethod}, {withCredentials: true})
    }
    downgradeToFree(user: UserModel):Observable<{endDate: number}> {
        this.mixpanel.track('Downgraded Membership')
        return this.http.post<{endDate: number}>(DOWNGRADE_TO_FREE_URL, user, {withCredentials: true})
    }

    resumeSubscription(): Observable<void> {
        return this.http.post<void>(`${STRIPE_URL}/subscription/me/resume`, {}, {withCredentials: true})
    }

    applyCoupon(couponCode: string): Observable<ApplyCouponResponse> {
        this.mixpanel.track('Kept Plan With Coupon')
        return this.http.post<ApplyCouponResponse>(APPLY_COUPON_URL, {coupon: couponCode}, {withCredentials: true})
    }

    getUserProrationDetails(stripePlanId: string): Observable<{prorate: number}> {
        const params = {stripePlanId : stripePlanId}
        return this.http.get<{prorate: number}>(GET_PRORATION, {params, withCredentials: true})
    }

    getUserCurrentPlanInfo(): Observable<MembershipInfo> {
        return this.http.get<MembershipInfo>(USER_MEMBERSHIP_INFO, {withCredentials: true});
    }

    pausePlan(durationMonths: number): Observable<any>{
        this.mixpanel.track('Paused Membership')
        return this.http.post(`${STRIPE_URL}/subscriptions/me`, {durationMonths}, {withCredentials: true})
    }

    public getUserPerkProductSubscription(product: string): Observable<any> {
        const params = {product: product}
        return this.http.get(`${PRODUCTS_URL}/me`, {params, withCredentials: true})
    }
}
export function trialCodeToDays(
    code: string,
    opts?: { dash?: boolean }
): string {
    //optional param to display "x-day" or "x day"
    const dash = opts?.dash !== false ? '-' : ' ';

    switch (code) {
        case TrialCodes.TwoWeekTrial: return `14${dash}day`;
        case TrialCodes.OneMonthTrial: return `30${dash}day`;
        case TrialCodes.ThreeMonthTrial: return `3${dash}month`;
        case TrialCodes.SixMonthTrial: return `6${dash}month`;
        default: return '';
    }
}

